import Popover from "@mui/material/Popover";
import { styled } from "@mui/material/styles";
import Badge from "@mui/material/Badge";
import React, { useEffect, useState } from "react";
import { Tabs } from "components-redesign/Tabs";
import { DatagridFilter, DatagridFilterOptions } from "./types";
import { FilterContent } from "./FilterContent";
import { FilterLoading } from "./FilterLoading";
import { ReactComponent as FilterAppliedIcon } from "./icons/filter-applied.svg";
import { ReactComponent as FilterIcon } from "./icons/filter.svg";

const FilterIconContainer = styled("div")({
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  marginTop: "6px",
  color: "#6750A4",
});

const FilterDropdown = styled("div")({
  width: "420px",
  maxHeight: "450px",
  padding: "16px",
  backgroundColor: "white",
  borderRadius: "8px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const TabsContainer = styled("div")({
  marginBottom: "8px",
  borderBottom: "1px solid #eee",
});

export interface FilterGroup {
  name: string;
  field: string;
  options: DatagridFilterOptions;
}

export interface MultiTabDropdownFilterProps {
  filterGroups: FilterGroup[];
  selectedValues: { [field: string]: string[] };
  onChange: (values: { [field: string]: string[] }) => void;
  hideFilterCount?: boolean;
  singleGroupSelection?: boolean;
}

export const MultiTabDropdownFilter: React.FC<MultiTabDropdownFilterProps> = ({
  filterGroups,
  selectedValues,
  onChange,
  hideFilterCount,
  singleGroupSelection,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchTerms, setSearchTerms] = useState<{ [field: string]: string }>(
    {}
  );
  const [tempSelectedValues, setTempSelectedValues] = useState<{
    [field: string]: string[];
  }>(selectedValues);
  const filterButtonRef = React.useRef<HTMLDivElement>(null);

  const [resolvedOptionsMap, setResolvedOptionsMap] = useState<{
    [field: string]: DatagridFilter[];
  }>({});
  const [loadingFields, setLoadingFields] = useState<Set<string>>(new Set());
  const resolvedFromRef = React.useRef<{
    [field: string]: DatagridFilterOptions;
  }>({});

  useEffect(() => {
    filterGroups.forEach((group) => {
      if (resolvedFromRef.current[group.field] === group.options) return;

      resolvedFromRef.current[group.field] = group.options;

      if (Array.isArray(group.options)) {
        setResolvedOptionsMap((prev) => ({
          ...prev,
          [group.field]: group.options as DatagridFilter[],
        }));
        setLoadingFields((prev) => {
          if (!prev.has(group.field)) return prev;
          const next = new Set(prev);
          next.delete(group.field);
          return next;
        });
      } else {
        const pending = group.options;
        setLoadingFields((prev) => new Set(prev).add(group.field));

        pending.then((result) => {
          // a newer options reference already took over
          if (resolvedFromRef.current[group.field] !== pending) return;

          setResolvedOptionsMap((prev) => ({ ...prev, [group.field]: result }));
          setLoadingFields((prev) => {
            const next = new Set(prev);
            next.delete(group.field);
            return next;
          });
        });
      }
    });
  }, [filterGroups]);

  const handleFilterClick = () => {
    setTempSelectedValues(selectedValues);
    setIsOpen(true);
  };

  const handleFilterClose = () => {
    setIsOpen(false);
    setSearchTerms({});
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleApply = () => {
    onChange(tempSelectedValues);
    handleFilterClose();
  };

  const handleClear = () => {
    const cleared = { ...tempSelectedValues };
    filterGroups.forEach((group) => {
      cleared[group.field] = [];
    });
    setTempSelectedValues(cleared);
  };

  const handleSelectionChange = (field: string, values: string[]) => {
    setTempSelectedValues((prev) => {
      // only the tab the user last picked on applies, so drop the other tabs
      const base = singleGroupSelection ? {} : prev;

      return {
        ...base,
        [field]: values,
      };
    });
  };

  const handleSearchChange = (field: string, value: string) => {
    setSearchTerms((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const hasActiveFilters = Object.values(selectedValues).some(
    (values) => values.length > 0
  );

  const currentGroup = filterGroups[selectedTab];
  const currentSelectedValues = tempSelectedValues[currentGroup?.field] || [];
  const currentSearchTerm = searchTerms[currentGroup?.field] || "";
  const currentResolvedOptions = resolvedOptionsMap[currentGroup?.field];
  const currentLoading = currentGroup
    ? loadingFields.has(currentGroup.field)
    : false;

  const tabs = filterGroups.map((group, index) => {
    const count = tempSelectedValues[group.field]?.length || 0;
    return {
      label: count > 0 ? `${group.name} (${count})` : group.name,
      value: index,
    };
  });
  const filtersCount = Object.values(selectedValues).flat().length;

  return (
    <>
      <Badge
        badgeContent={filtersCount}
        sx={{
          "& .MuiBadge-badge": {
            color: "#ffffff",
            backgroundColor: "#6750A4",
          },
        }}>
        <FilterIconContainer
          ref={filterButtonRef}
          onClick={handleFilterClick}>
          {hasActiveFilters ? <FilterAppliedIcon /> : <FilterIcon />}
        </FilterIconContainer>
      </Badge>

      <Popover
        open={isOpen}
        anchorEl={filterButtonRef.current}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}>
        <FilterDropdown>
          <TabsContainer>
            <Tabs
              tabs={tabs}
              value={selectedTab}
              onChange={handleTabChange}
            />
          </TabsContainer>

          {currentGroup &&
            (currentLoading || !currentResolvedOptions ? (
              <FilterLoading />
            ) : (
              <FilterContent
                options={currentResolvedOptions}
                selectedValues={currentSelectedValues}
                onSelectionChange={(values) =>
                  handleSelectionChange(currentGroup.field, values)
                }
                onApply={handleApply}
                onClear={handleClear}
                hideFilterCount={hideFilterCount}
                searchTerm={currentSearchTerm}
                onSearchChange={(value) =>
                  handleSearchChange(currentGroup.field, value)
                }
              />
            ))}
        </FilterDropdown>
      </Popover>
    </>
  );
};
